/**
 * Reusable PrecisionBadge component with tooltip support
 * Displays precision level with context-specific explanations
 * Supports both hover (desktop) and tap (mobile) interactions
 * Can render as a badge (with label) or icon-only
 */

import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Target, CircleDot, MoveHorizontal, HelpCircle } from 'lucide-react';

// Custom "c" icon for circa (traditional abbreviation)
function CircaIcon({ size = 12, className = '' }: { size?: number; className?: string }) {
  return (
    <span
      className={`inline-flex items-center justify-center font-serif italic font-semibold ${className}`}
      style={{
        fontSize: `${size}px`,
        width: `${size}px`,
        height: `${size}px`,
        lineHeight: 1
      }}
    >
      c
    </span>
  );
}

type PrecisionContext =
  | 'coordinates'     // Geographic coordinate precision
  | 'dating'          // Date precision
  | 'general';        // Generic precision

interface PrecisionBadgeProps {
  precision: DatePrecision | LocationPrecision | string;
  context?: PrecisionContext;
  className?: string;
  variant?: 'badge' | 'icon';  // 'badge' shows label, 'icon' shows icon only
  size?: number;               // Icon size (only used for 'icon' variant)
  showTooltip?: boolean;       // Whether to show tooltip (default: true)
}

const TOOLTIP_TEXT: Record<PrecisionContext, string> = {
  coordinates: 'Technical accuracy of the coordinates',
  dating: 'How precise the date is',
  general: 'Level of precision'
};

const PRECISION_LABELS: Record<string, string> = {
  // Date precision
  exact: 'Exact',
  approximate: 'Approximate',
  circa: 'Circa',
  range: 'Range',
  // Location precision
  uncertain: 'Uncertain'
};

const PRECISION_ICONS = {
  exact: Target,
  approximate: CircleDot,
  circa: CircaIcon,
  range: MoveHorizontal,
  uncertain: HelpCircle
};

const PRECISION_COLORS = {
  exact: 'text-muted-foreground',
  approximate: 'text-muted-foreground',
  circa: 'text-muted-foreground',
  range: 'text-muted-foreground',
  uncertain: 'text-muted-foreground'
};

const PRECISION_DESCRIPTIONS: Record<string, Record<PrecisionContext, string>> = {
  // Date precision
  exact: {
    coordinates: 'GPS-level precision, within 100m',
    dating: 'Specific year or narrow range known',
    general: 'Very precise'
  },
  approximate: {
    coordinates: 'Within 1km - general area known',
    dating: 'Within approximately 5 years',
    general: 'Reasonably precise'
  },
  circa: {
    coordinates: 'Not applicable to coordinates',
    dating: 'Within approximately 10-20 years',
    general: 'Moderately precise'
  },
  range: {
    coordinates: 'Not applicable to coordinates',
    dating: 'Broader time span or period',
    general: 'Broad range'
  },
  uncertain: {
    coordinates: 'Within ~10km - approximate region',
    dating: 'Not applicable to dating',
    general: 'Low precision'
  }
};

export function PrecisionBadge({
  precision,
  context = 'general',
  className = '',
  variant = 'badge',
  size = 16,
  showTooltip = true
}: PrecisionBadgeProps) {
  const [open, setOpen] = useState(false);
  const tooltipText = TOOLTIP_TEXT[context];
  const label = PRECISION_LABELS[precision] || precision;
  const description = PRECISION_DESCRIPTIONS[precision]?.[context];
  const Icon = PRECISION_ICONS[precision as keyof typeof PRECISION_ICONS] || HelpCircle;
  const colorClass = PRECISION_COLORS[precision as keyof typeof PRECISION_COLORS] || 'text-muted-foreground';

  // Icon-only variant
  if (variant === 'icon') {
    const iconSize = size;
    const icon = precision === 'circa' ? (
      <CircaIcon
        className={`${colorClass} ${className} ${showTooltip ? 'cursor-help' : ''}`}
        size={iconSize}
      />
    ) : (
      <Icon
        className={`${colorClass} ${className} ${showTooltip ? 'cursor-help' : ''}`}
        size={iconSize}
      />
    );

    if (!showTooltip) {
      return icon;
    }

    return (
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <span onClick={() => setOpen(!open)}>
            {icon}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-1">
            <p className="font-semibold">{label} Precision</p>
            <p className="text-xs opacity-90">{tooltipText}</p>
            {description && <p className="text-xs opacity-90">{description}</p>}
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }

  // Badge variant (default)
  const badgeContent = (
    <Badge
      variant="secondary"
      className={`text-xs ${className} cursor-help flex items-center gap-1`}
      onClick={() => setOpen(!open)}
    >
      {precision === 'circa' ? (
        <CircaIcon size={12} />
      ) : (
        <Icon size={12} />
      )}
      {label} Precision
    </Badge>
  );

  if (!showTooltip) {
    return badgeContent;
  }

  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        {badgeContent}
      </TooltipTrigger>
      <TooltipContent>
        <div className="space-y-1">
          <p className="font-semibold">{label} Precision</p>
          <p className="text-xs opacity-90">{tooltipText}</p>
          {description && <p className="text-xs opacity-90">{description}</p>}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
